/**
 * Copyright 2025 SweetCode. All rights reserved.
 *
 * Microsoft Ads browser pixel functions (free tier)
 *
 * Everything the UET tag needs to load and fire browser events. The
 * Conversions API helpers (visitor ID, user data) live in functions_premium.js.
 * */

(function (pmw, undefined) {

	pmw.load_bing_pixel          = () => {

		try {
			// @formatter:off
			window.uetq = window.uetq || [];

			(function(w,d,t,r,u){var f,n,i;w[u]=w[u]||[],f=function(){var o={ti:pmwDataLayer.pixels.bing.uet_tag_id};o.q=w[u],w[u]=new UET(o),w[u].push("pageLoad")},n=d.createElement(t),n.src=r,n.async=1,n.onload=n.onreadystatechange=function(){var s=this.readyState;s&&s!=="loaded"&&s!=="complete"||(f(),n.onload=n.onreadystatechange=null)},i=d.getElementsByTagName(t)[0],i.parentNode.insertBefore(n,i)})(window,document,"script","//bat.bing.com/bat.js","uetq");
			// @formatter:on

			init_bing_consent_mode();
			set_enhanced_conversion_data();

			pmwDataLayer.pixels.bing.loaded = true;

		} catch (e) {
			console.error(e);
		}
	}
	/**
	 * Initialize the Microsoft Ads Consent Mode
	 *
	 * Source: https://help.ads.microsoft.com/apex/index/3/en/60119
	 * */
	const init_bing_consent_mode = () => {

		if (!pmwDataLayer?.pixels?.bing?.consent_mode?.is_active) return;

		const consentSettings = {
			"ad_storage": pmw.consent.categories.get().marketing ? "granted" : "denied",
		};

		if (!pmw.consent.categories.get().marketing) {
			// set the _uetmsdns session cookie to prevent the Microsoft Ads pixel from sending events
			pmw.setCookie("_uetmsdns");
		}

		window.uetq.push("consent", "default", consentSettings);

		pmw.console.log("Microsoft Ads Consent Mode settings:", consentSettings);
	};

	pmw.canLoadBingPixel = () => {

		// Always returns true if Microsoft Ads Consent Mode is active
		if (pmwDataLayer?.pixels?.bing?.consent_mode?.is_active) {
			pmw.console.log("Microsoft Ads Consent Mode is active. Loading the Microsoft Ads pixel.");
			return true;
		}

		if (pmw.consent.canPixelBeFired("marketing", "Microsoft Ads")) {
			return true;
		}

		return false;
	};

	/**
	 * Set enhanced conversion data
	 *
	 * Enhanced Conversions is a Pro setting, and the hashed user data it
	 * needs is only in the data layer on Pro, so this is a no-op on free.
	 *
	 * https://help.ads.microsoft.com/apex/index/3/en/60178
	 */
	const set_enhanced_conversion_data = () => {

		// Only continue if it is an order
		if (!pmwDataLayer?.order) return;

		// Only continue if the enhanced conversions are enabled
		if (!pmwDataLayer?.pixels?.bing?.enhanced_conversions) return;

		// Only set the data if the user has an email or phone number
		if (!pmwDataLayer?.user?.email?.sha256 && !pmwDataLayer?.user?.phone?.sha256?.e164) return;

		let pid = {};

		if (pmwDataLayer?.user?.email?.sha256) {
			pid.em = pmwDataLayer.user.email.sha256;
		}

		if (pmwDataLayer?.user?.phone?.sha256?.e164) {
			pid.ph = pmwDataLayer.user.phone.sha256.e164;
		}

		window.uetq.push("set", {
			"pid": pid,
		});
	};

	pmw.bing_purchase_ecomm_prodids = () => {

		let prodIds = [];

		Object.values(pmwDataLayer.order.items).forEach((orderItem) => {

			const productId = pmwDataLayer?.shop?.variations_output && 0 !== orderItem.variation_id ? orderItem.variation_id : orderItem.id;
			const product   = pmw.getDataLayerProduct(productId);

			// A product deleted since the order was placed has no data left to
			// report. Skip the line rather than lose the whole conversion.
			if (!product) {
				pmw.console.warn("Microsoft Ads: skipped an order item without product data: " + productId);
				return;
			}

			prodIds.push(String(product.dyn_r_ids[pmwDataLayer.pixels.bing.dynamic_remarketing.id_type]));
		});

		return prodIds;
	};

	pmw.bingPurchaseItems = () => {

		let orderItems = [];

		Object.values(pmwDataLayer.order.items).forEach(item => {

			const item_id = pmwDataLayer?.shop?.variations_output && 0 !== item.variation_id ? item.variation_id : item.id;
			const id      = pmw.getProductIdForSpecificPixel(pmw.getDataLayerProduct(item_id), "bing");

			// A product deleted since the order was placed has no data left to
			// report. Skip the line rather than lose the whole conversion.
			if (null === id) {
				pmw.console.warn("Microsoft Ads: skipped an order item without product data: " + item_id);
				return;
			}

			orderItems.push({
				quantity: item.quantity,
				price   : item.price,
				id      : id,
			});
		});

		return orderItems;
	};

	/**
	 * Resolve the Microsoft click ID (msclkid).
	 *
	 * Microsoft appends msclkid to the landing page URL after an ad click when
	 * auto-tagging is enabled. It is the strongest attribution signal the
	 * Conversions API accepts, so the Pixel Manager captures the URL parameter
	 * itself (platform event_listeners.js) and keeps it for the session.
	 *
	 * https://learn.microsoft.com/en-us/advertising/guides/uet-conversion-api-integration#click-id-msclkid
	 *
	 * @return {string|null}
	 */
	pmw.getMicrosoftClickId = () => {
		return pmw.retrieveData("msclkid") || null;
	};

	/**
	 * Resolve the category of the current page for Microsoft's dynamic
	 * remarketing.
	 *
	 * Only a product category page has one. The Pixel Manager already builds a
	 * hierarchical slug path for the list ID ("product_category.clothing.hoodies"),
	 * so the category is the part after the prefix. A product page reports its
	 * product ID instead, which is the signal Microsoft uses there.
	 *
	 * @return {string|null}
	 */
	pmw.getBingEcommCategory = () => {

		const listId = pmwDataLayer?.shop?.list_id;

		if (typeof listId !== "string" || !listId.startsWith("product_category.")) return null;

		const category = listId.slice("product_category.".length);

		return category || null;
	};

	pmw.getBingDataLayerProducts = () => {

		let productIds = [];

		Object.values(pmwDataLayer.products).forEach((product) => {
			productIds.push(String(product.dyn_r_ids[pmwDataLayer.pixels.bing.dynamic_remarketing.id_type]));
		});

		return productIds;
	};

	/**
	 * Check if the Microsoft Ads Pixel can be fired
	 *
	 * @return {boolean}
	 */
	pmw.canFireUetq = () => {

		if (!pmwDataLayer?.pixels?.bing?.loaded) return false;

		// If the user has not given consent for marketing, return false
		if (!pmw.consent.categories.get().marketing) return false;

		return true;
	};

	/**
	 * Check if the Microsoft Ads Pixel can not be fired
	 *
	 * @return {boolean}
	 */
	pmw.canNotFireUetq = () => {
		return !pmw.canFireUetq();
	};

}(window.pmw = window.pmw || {}));
